import React from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { 
  Box, 
  AppBar, 
  Toolbar, 
  Typography, 
  IconButton, 
  useTheme, 
  alpha,
  Tooltip,
  Button,
  Tab,
  Tabs,
  Badge,
} from '@mui/material';
import { 
  Logout,
  Dashboard,
  Chat,
  VerifiedUser,
  Mail,
  Notifications,
  ConfirmationNumber,
  Schedule,
  Language,
  GetApp,
  Settings,
  Business,
  People,
} from '@mui/icons-material';
import { useTheme as useAppTheme } from '../../contexts/AppThemeProvider';
import { useLayout } from '../../contexts/LayoutContext';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store';
import { logout } from '../../store/slices/authSlice';
import { socketService } from '../../services/socket';

const TopNavLayout: React.FC = () => {
  const theme = useTheme();
  const { mode } = useAppTheme();
  const { preferences } = useLayout();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.auth.user);
  const [installPrompt, setInstallPrompt] = React.useState<any>(null);
  const [canInstall, setCanInstall] = React.useState(false);
  
  const pendingChatRequests = useSelector((state: RootState) => state.chat?.pendingRequests || []);
  const unreadMessages = useSelector((state: RootState) => state.chat?.unreadCount || 0);
  const chatBadgeCount = pendingChatRequests.length + unreadMessages;
  
  const corporateGreen = '#289546';
  const classicTopBar = '#2b3e7d';
  const headerBackground = preferences.themePreset === 'classic' 
    ? classicTopBar
    : mode === 'dark' 
      ? `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.95)} 0%, ${alpha(theme.palette.background.default, 0.98)} 100%)`
      : corporateGreen;

  const tabs = [
    { path: '/dashboard', label: 'Dashboard', icon: <Dashboard /> },
    { path: '/clients', label: 'Clients', icon: <Business /> },
    { path: '/users', label: 'Users', icon: <People /> },
    { path: '/chat', label: 'Chat', icon: chatBadgeCount > 0 ? <Badge badgeContent={chatBadgeCount} color="error"><Chat /></Badge> : <Chat /> },
    { path: '/secure-verification', label: 'Verify', icon: <VerifiedUser /> },
    { path: '/secure-messages', label: 'Messages', icon: <Mail /> },
    { path: '/push-notifications', label: 'Push', icon: <Notifications /> },
    { path: '/tickets', label: 'Tickets', icon: <ConfirmationNumber /> },
    { path: '/meetings', label: 'Meetings', icon: <Schedule /> },
    { path: '/portal', label: 'Portal', icon: <Language /> },
  ];

  const currentTab = tabs.findIndex(tab => location.pathname.startsWith(tab.path));

  const handleLogout = () => {
    socketService.disconnect();
    dispatch(logout());
    navigate('/login');
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    const tab = tabs[newValue];
    
    if (tab.path === '/portal') {
      socketService.emit('openPortal', (response: { success: boolean; portalUrl?: string; error?: string }) => {
        if (response.success && response.portalUrl) {
          const link = document.createElement('a');
          link.href = response.portalUrl;
          link.target = '_blank';
          link.rel = 'noopener noreferrer';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } else {
          console.error('Failed to open portal:', response.error);
        }
      });
    } else {
      navigate(tab.path);
    }
  };

  React.useEffect(() => {
    const handleBeforeInstallPrompt = (e: any) => {
      e.preventDefault();
      setInstallPrompt(e);
      setCanInstall(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    if (window.matchMedia('(display-mode: standalone)').matches) {
      setCanInstall(false);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!installPrompt) return;
    
    installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setCanInstall(false);
    }
    setInstallPrompt(null);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', margin: 0, padding: 0, overflow: 'hidden' }}>
      <AppBar 
        position="sticky" 
        elevation={2}
        sx={{ 
          background: headerBackground,
          border: 'none !important',
          borderBottom: 'none !important',
          borderTop: 'none !important',
          borderLeft: 'none !important',
          borderRight: 'none !important',
          borderRadius: 0,
          boxShadow: mode === 'dark' 
            ? '0 2px 8px rgba(0,0,0,0.3)' 
            : '0 2px 8px rgba(0,0,0,0.1)',
          outline: 'none',
          backdropFilter: mode === 'dark' ? 'blur(10px)' : 'none',
          '@media (display-mode: window-controls-overlay)': {
            WebkitAppRegion: 'drag',
            appRegion: 'drag',
          }
        }}
      >
        <Toolbar sx={{ 
          minHeight: '64px !important', 
          px: 2,
          py: 1
        }}>
          <Typography 
            variant="h5" 
            component="div" 
            sx={{ 
              fontWeight: 700,
              letterSpacing: -0.3,
              color: mode === 'dark' ? theme.palette.primary.light : '#ffffff',
              fontSize: '1.2rem',
              minWidth: '200px',
              fontFamily: '"Inter", "SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              textShadow: mode === 'dark' ? 'none' : '0 1px 2px rgba(0,0,0,0.2)',
              '&::before': {
                content: '""',
                display: 'inline-block',
                width: 8,
                height: 24,
                background: mode === 'dark' 
                  ? `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`
                  : 'linear-gradient(135deg, #fff 0%, rgba(255,255,255,0.7) 100%)',
                borderRadius: 2,
                boxShadow: mode === 'dark' 
                  ? `0 0 12px ${alpha(theme.palette.primary.main, 0.4)}`
                  : '0 0 12px rgba(255,255,255,0.4)',
              }
            }}
          >
            IT Control Panel
          </Typography>

          <Box sx={{ 
            flexGrow: 1, 
            display: 'flex', 
            justifyContent: 'center',
            mx: 2,
          }}>
            <Tabs 
              value={currentTab >= 0 ? currentTab : false}
              onChange={handleTabChange}
              sx={{
                '@media (display-mode: window-controls-overlay)': {
                  WebkitAppRegion: 'no-drag',
                  appRegion: 'no-drag',
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: mode === 'dark' ? theme.palette.primary.main : '#ffffff',
                  height: 3,
                  borderRadius: '3px 3px 0 0',
                },
                '& .MuiTab-root': {
                  color: mode === 'dark' 
                    ? alpha(theme.palette.text.primary, 0.7)
                    : alpha('#ffffff', 0.7),
                  minHeight: '48px',
                  minWidth: '80px',
                  px: 2,
                  py: 0.5,
                  position: 'relative',
                  transition: 'all 0.3s ease',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: 0,
                    left: '20%',
                    right: '20%',
                    height: 3,
                    backgroundColor: mode === 'dark'
                      ? alpha(theme.palette.primary.main, 0.2)
                      : alpha('#ffffff', 0.2),
                    borderRadius: '3px 3px 0 0',
                    transition: 'all 0.3s ease',
                  },
                  '&:hover': {
                    color: mode === 'dark' ? theme.palette.text.primary : '#ffffff',
                    backgroundColor: mode === 'dark' 
                      ? alpha(theme.palette.primary.main, 0.1)
                      : alpha('#ffffff', 0.1),
                    transform: 'translateY(-2px)',
                    '&::after': {
                      backgroundColor: mode === 'dark'
                        ? alpha(theme.palette.primary.main, 0.3)
                        : alpha('#ffffff', 0.3),
                      left: '10%',
                      right: '10%',
                    },
                  },
                  '&.Mui-selected': {
                    color: mode === 'dark' ? theme.palette.primary.light : '#ffffff',
                    backgroundColor: mode === 'dark'
                      ? alpha(theme.palette.primary.main, 0.15)
                      : alpha('#ffffff', 0.15),
                    borderRadius: '8px 8px 0 0',
                    backdropFilter: 'blur(10px)',
                    boxShadow: mode === 'dark'
                      ? `0 0 20px ${alpha(theme.palette.primary.main, 0.1)}`
                      : `0 0 20px ${alpha('#ffffff', 0.1)}`,
                    '&::after': {
                      display: 'none',
                    },
                  },
                },
              }}
            >
              {tabs.map((tab, index) => (
                <Tab
                  key={tab.path}
                  label={
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center',
                      gap: 0.5,
                    }}>
                      {tab.icon}
                      <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 500 }}>
                        {tab.label}
                      </Typography>
                    </Box>
                  }
                />
              ))}
            </Tabs>
          </Box>
          
          {user && (
            <Typography 
              variant="body2" 
              sx={{ 
                mr: 3,
                color: mode === 'dark' 
                  ? alpha(theme.palette.text.primary, 0.9)
                  : alpha('#ffffff', 0.9),
                alignSelf: 'center',
                '@media (display-mode: window-controls-overlay)': {
                  alignSelf: 'flex-end',
                  mb: 0.5,
                }
              }}
            >
              {user.name || user.email}
            </Typography>
          )}

          {canInstall && (
            <Tooltip title="Install app">
              <IconButton 
                onClick={handleInstallClick}
                size="small"
                sx={{ 
                  color: mode === 'dark' ? theme.palette.text.primary : '#ffffff',
                  mr: 1,
                  alignSelf: 'center',
                  '@media (display-mode: window-controls-overlay)': {
                    WebkitAppRegion: 'no-drag',
                    appRegion: 'no-drag',
                    alignSelf: 'flex-end',
                    mb: 0.5,
                  }
                }}
              >
                <GetApp />
              </IconButton>
            </Tooltip>
          )}

          <Tooltip title="Settings">
            <IconButton 
              onClick={() => navigate('/settings')}
              size="small"
              sx={{ 
                color: mode === 'dark' ? theme.palette.text.primary : '#ffffff',
                mr: 1,
                alignSelf: 'center',
                '@media (display-mode: window-controls-overlay)': {
                  WebkitAppRegion: 'no-drag',
                  appRegion: 'no-drag',
                  alignSelf: 'flex-end',
                  mb: 0.25,
                }
              }}
            >
              <Settings />
            </IconButton>
          </Tooltip>

          <Button
            variant="text"
            size="small"
            startIcon={<Logout />}
            onClick={handleLogout}
            sx={{ 
              color: mode === 'dark' ? theme.palette.text.primary : '#ffffff',
              alignSelf: 'center',
              py: 0.5,
              '&:hover': {
                background: mode === 'dark'
                  ? alpha(theme.palette.primary.main, 0.1)
                  : alpha('#ffffff', 0.1),
              },
              '@media (display-mode: window-controls-overlay)': {
                WebkitAppRegion: 'no-drag',
                appRegion: 'no-drag',
                alignSelf: 'flex-end',
                mb: 0.25,
                py: 0.25,
              }
            }}
          >
            Logout
          </Button>
        </Toolbar>
      </AppBar>

      <Box sx={{ 
        flex: 1,
        overflowY: 'auto',
        overflowX: 'hidden',
        position: 'relative'
      }}>
        <Outlet />
      </Box>
    </Box>
  );
};

export default TopNavLayout;